// Primitivos del cuerpo de los cajones de detalle (`.fk-card`, `.fk-kv` del mock): la tarjeta con
// encabezado en versalitas y el renglón clave/valor. Salieron de la ficha de crédito de Cartera.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

const NO_VALUE = '—';

export function DetailCard({
    title,
    meta,
    flush = false,
    footnote,
    children,
}: {
    title: ReactNode;
    meta?: ReactNode;
    /** Sin padding interno: para las tablas que van al ras del borde de la card. */
    flush?: boolean;
    /** UNA sola nota al pie por card, que agrupa lo que esta card no pudo mostrar. */
    footnote?: ReactNode;
    children: ReactNode;
}) {
    return (
        <section className="mb-2.5 overflow-hidden rounded-md border border-border bg-card">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.04em]">{title}</h3>
                {meta ? (
                    <span className="ml-auto text-[10.5px] tabular-nums text-muted-foreground">
                        {meta}
                    </span>
                ) : null}
            </header>
            <div className={flush ? '' : 'px-3 py-2'}>{children}</div>
            {footnote === undefined ? null : (
                <p className="border-t border-border/50 bg-muted/20 px-3 py-1.5 text-[9.5px] leading-snug text-muted-foreground">
                    {footnote}
                </p>
            )}
        </section>
    );
}

/** Marca corta de «este dato no tiene fuente». El motivo completo va en el `title`. */
export function GapMark({ note, label = 'sin fuente' }: { note: string; label?: string }) {
    return (
        <span
            title={note}
            className="cursor-help whitespace-nowrap rounded-sm bg-muted px-1 py-[1px] text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
        >
            {label}
        </span>
    );
}

/** Renglón clave/valor. Con `note` el valor queda en guion con su marca y el motivo en el `title`. */
export function DetailKeyValue({
    label,
    value,
    note,
    mark,
    mono = false,
    plain = false,
}: {
    label: ReactNode;
    value?: ReactNode;
    note?: string;
    /** Distintivo que acompaña al valor sin taparlo. */
    mark?: ReactNode;
    mono?: boolean;
    /** El valor va con peso 500 en vez del resaltado de las demás tarjetas. */
    plain?: boolean;
}) {
    return (
        <div className="flex items-baseline justify-between gap-x-3 border-b border-border/40 py-[5px] last:border-b-0">
            <span className="text-[10.5px] text-muted-foreground">{label}</span>
            <span className="flex min-w-0 items-center gap-1.5 text-right">
                <span
                    className={cn(
                        'min-w-0 break-words text-[11.5px] tabular-nums',
                        plain ? 'font-medium' : 'font-semibold',
                        mono && 'font-mono',
                        note && 'text-muted-foreground',
                    )}
                >
                    {note ? NO_VALUE : (value ?? NO_VALUE)}
                </span>
                {note ? <GapMark note={note} /> : null}
                {mark}
            </span>
        </div>
    );
}

export interface DetailTab<K extends string = string> {
    key: K;
    label: string;
}

/** La tira de pestañas del cajón: subrayada y envuelve en vez de scrollear. */
export function DetailTabs<K extends string>({
    tabs,
    active,
    onChange,
}: {
    tabs: readonly DetailTab<K>[];
    active: K;
    onChange: (key: K) => void;
}) {
    return (
        <div
            className="flex flex-none flex-wrap gap-0.5 border-b border-border px-3"
            role="tablist"
        >
            {tabs.map((tab) => {
                const isActive = tab.key === active;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => {
                            onChange(tab.key);
                        }}
                        className={cn(
                            'whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[11.5px] font-medium transition-colors',
                            isActive
                                ? 'border-primary text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}
